import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button, Loader, Modal, Table } from '@itds/elements/index.js';
import { ZeroState } from '@itds/components/index.js';
import { QUOTE_ACTIONS } from '@itds/Constants';

import Toolbar from '@brokerage/components/Toolbar';
import EditQuoteActionModal from '@brokerage/components/EditQuoteActionModal';

import { AgencyContext } from '@brokerage/contexts/AgencyContext';

import { Api } from '@brokerage/utils/Api';

const CustomActions = ({ mobile_app, setErrorMessage, setSuccessMessage }) => {
  const { agency } = useContext(AgencyContext);

  const [loading, setLoading] = useState(true);
  const [custom_actions, setCustomActions] = useState([]);
  const [modal, setModal] = useState(null);
  const [action, setAction] = useState(null);

  const filteredQuoteActions = QUOTE_ACTIONS?.filter(qa => qa.products?.includes('MobileApp')) || [];

  useEffect(() => {
    loadCustomActions();
  }, []);

  const loadCustomActions = () => {
    setLoading(true);

    Api.loadAgencyMobileAppCustomActions(mobile_app.guid).then(response => {
      setCustomActions(response.data);

      setLoading(false);
    });
  };

  const showAction = action => {
    setAction(action);
    setModal('edit');
  };

  const handleSaveClick = action => {
    action.mobile_app_guid = mobile_app.guid;

    Api.saveMobileAppCustomAction(action.id, action)
      .then(() => {
        loadCustomActions();
        setModal(null);
        setSuccessMessage('Quote Action updated');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setModal(null);
        setErrorMessage('Unable to update the Quote Action at this time');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  const handleDelete = (action, e) => {
    setAction(action);
    setModal('delete');

    e.stopPropagation();
  };

  const confirmDelete = () => {
    window.scrollTo(0, 0);

    Api.deleteMobileAppCustomAction(action.id)
      .then(() => {
        setModal(null);
        setAction(null);
        loadCustomActions();
        setSuccessMessage('Quote Action deleted');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setAction(null);
        setModal(null);
        setErrorMessage('Unable to delete the Quote Action at this time');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  const action_types = [
    {
      label: 'Basic',
      options: filteredQuoteActions.filter(item => item.group === 'Basic')
    },
    {
      label: 'Defaults',
      options: filteredQuoteActions.filter(item => item.group === 'Default')
    },
    {
      label: 'Instant Issue',
      options: filteredQuoteActions.filter(item => item.group === 'Instant Issue')
    },
    {
      label: 'Integrations',
      options: filteredQuoteActions.filter(item => item.group === 'Integration')
    },
    {
      label: 'E-Tickets',
      options: filteredQuoteActions.filter(item => {
        if (item.group === 'E-Ticket' || (item.value === 'applicant' && agency.enable_applicint)) {
          return true;
        }
        return false;
      })
    },
    {
      label: 'E-Apps',
      options: filteredQuoteActions.filter(item => {
        if (item.group === 'E-App' || (item.value === 'psg_eapp' && !agency.enable_psg_eapps)) {
          return true;
        }
        return false;
      })
    },
    {
      label: '3rd Party Forms',
      options: filteredQuoteActions.filter(item => item.group === 'Form')
    }
  ];

  return loading ? (
    <Loader />
  ) : (
    <>
      {modal === 'edit' ? (
        <EditQuoteActionModal
          actionTypes={action_types}
          agency={agency}
          availableCarrierIds={mobile_app.carrier_ids ? mobile_app.carrier_ids : []}
          availableProductIds={mobile_app.product_ids ? mobile_app.product_ids : []}
          beta={false} // TODO: pull this from quoter object
          hideIcon={true} // Don't know if we need this
          onClose={() => {
            setModal(null);
            setAction(null);
          }}
          onSave={handleSaveClick}
          quoteAction={action}
          showFaceAmounts={true} // Don't know if we need this
          showOpenInModal={true} // Don't know if we need this
        />
      ) : null}

      {modal === 'delete' ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Delete',
              onClick: confirmDelete,
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={() => {
            setModal(null);
            setAction(null);
          }}
          title={`Delete "${action.label}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to delete the "{action.label}" quote action?
          </div>
        </Modal>
      ) : null}

      <Toolbar
        buttons={[
          {
            label: 'New Custom Action',
            onClick: () => {
              setAction(null);
              setModal('edit');
            }
          }
        ]}
      />

      {custom_actions.length ? (
        <Table
          columns={[
            {
              id: 'label',
              label: 'Label',
              render: action => {
                return <Link to={''}>{action.label ? action.label : 'N/A'}</Link>;
              }
            },
            {
              id: 'action_type',
              label: 'Type',
              render: action => {
                const quote_action = QUOTE_ACTIONS.find(qa => qa.value === action.action_type);

                return <>{quote_action ? quote_action.label : 'N/A'}</>;
              }
            },
            {
              id: 'order',
              label: 'Order'
            },
            {
              id: 'carriers',
              label: 'Carriers',
              render: action => {
                return <>{action.carrier_ids && action.carrier_ids.length} Carriers</>;
              }
            },
            {
              id: '',
              render: action => {
                return (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button color='RED' onClick={handleDelete.bind(null, action)} small={true}>
                      Delete
                    </Button>
                  </div>
                );
              }
            }
          ]}
          data={custom_actions}
          onRowClick={showAction}
        />
      ) : (
        <ZeroState
          icon='folder-open-outline'
          title="This Mobile ALQ doesn't have any Custom Actions"
        />
      )}
    </>
  );
};

CustomActions.propTypes = {
  mobile_app: PropTypes.object,
  setErrorMessage: PropTypes.func,
  setSuccessMessage: PropTypes.func
};

export default CustomActions;
